import { AuditLogItem, AuditValue } from '@services/audit-logs/interfaces/audit-logs.interfaces';

import {
  AUDIT_ACTION_LABELS,
  AUDIT_EMPTY_VALUE_LABEL,
  AUDIT_ENTITY_TYPE_LABELS,
  AUDIT_FIELD_LABELS,
  AUDIT_LOGS_TIME_ZONE,
  AUDIT_STATUS_VALUE_LABELS,
} from '@constants/audit-logs.constants';

const STATUS_FIELD_NAME = 'status';

const CHILE_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('es-CL', {
  timeZone: AUDIT_LOGS_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export interface AuditChangeLine {
  fieldLabel: string;
  before: string;
  after: string;
}

export function formatAuditDateTime(isoDate: string): string {
  return CHILE_DATE_TIME_FORMATTER.format(new Date(isoDate));
}

export function auditFieldLabel(fieldName: string): string {
  return AUDIT_FIELD_LABELS[fieldName] ?? fieldName;
}

export function formatAuditValue(value: AuditValue, fieldName?: string): string {
  if (value === null || value === '') {
    return AUDIT_EMPTY_VALUE_LABEL;
  }
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }
  if (fieldName === STATUS_FIELD_NAME && typeof value === 'string') {
    return AUDIT_STATUS_VALUE_LABELS[value] ?? value;
  }
  return String(value);
}

export function buildAuditChangeLines(auditLog: AuditLogItem): AuditChangeLine[] {
  return Object.entries(auditLog.changes ?? {}).map(([fieldName, fieldChange]) => ({
    fieldLabel: auditFieldLabel(fieldName),
    before: formatAuditValue(fieldChange.before, fieldName),
    after: formatAuditValue(fieldChange.after, fieldName),
  }));
}

/**
 * Plain text version of an audit entry, meant to be pasted into an email or WhatsApp message
 * for the developer. Includes the request id so it can be matched with the technical logs.
 */
export function buildAuditLogClipboardText(auditLog: AuditLogItem): string {
  const entityName = auditLog.entityLabel ? `${auditLog.entityLabel} (${auditLog.entityId})` : auditLog.entityId;
  const changedFields = auditLog.changedFields.map((fieldName) => auditFieldLabel(fieldName)).join(', ');
  const changeLines = buildAuditChangeLines(auditLog).map((changeLine) => `- ${changeLine.fieldLabel}: ${changeLine.before} → ${changeLine.after}`);

  return [
    `Fecha: ${formatAuditDateTime(auditLog.createdAt)}`,
    `Admin: ${auditLog.actorEmail}`,
    `Acción: ${AUDIT_ACTION_LABELS[auditLog.action] ?? auditLog.action}`,
    `${AUDIT_ENTITY_TYPE_LABELS[auditLog.entityType] ?? auditLog.entityType}: ${entityName}`,
    ...(changedFields ? [`Campos cambiados: ${changedFields}`] : []),
    ...(changeLines.length > 0 ? ['Cambios:', ...changeLines] : []),
    `Código de referencia: ${auditLog.requestId ?? '-'}`,
  ].join('\n');
}

export function toStartOfLocalDayIso(dateInputValue: string): string | null {
  return dateInputValue ? new Date(`${dateInputValue}T00:00:00`).toISOString() : null;
}

export function toEndOfLocalDayIso(dateInputValue: string): string | null {
  return dateInputValue ? new Date(`${dateInputValue}T23:59:59.999`).toISOString() : null;
}
